"use client"

import {
  navigateWithThreadPreservation,
  getPageHeader,
  Card,
  CardContent,
  Button,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  PageHeaderStack,
  PageHeaderActionPrimary,
  PageHeaderActions,
  PageLineTabs,
  PaneResponsiveCardGrid,
  DashboardSectionSkeleton,
  PaneListEmptyState,
  useArtifactCanvas,
  useListFilterBar,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@yeshuman/sdk'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { type Brief, useBriefs, useDeleteBrief, useUpdateBrief } from '../../hooks/use-briefs'
import { SimpleKanban, type SimpleKanbanCardProps } from '../employer/simple-kanban'
import { BriefListCard } from './brief-list-card'
import { BriefCanvasForm } from './brief-canvas-form'
import {
  computeRecruiterBriefStatusCounts,
  RECRUITER_BRIEFS_FILTER_DEFINITIONS,
  recruiterBriefStatusLabel,
  type RecruiterBriefsFilterState,
} from '../../lib/list-filters/recruiter-briefs'
import { Columns3, FileText, LayoutGrid, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

type BriefsView = 'list' | 'board'

const BRIEFS_VIEW_ITEMS = [
  { value: 'list' as const, label: 'List', icon: LayoutGrid },
  { value: 'board' as const, label: 'Board', icon: Columns3 },
] as const

const BRIEF_STATUS_COLUMNS: { id: Brief['status']; label: string }[] = [
  { id: 'draft', label: 'Draft' },
  { id: 'active', label: 'Active' },
  { id: 'closed', label: 'Closed' },
]

const briefId = (brief: Brief) => brief.id
const briefStatus = (brief: Brief) => brief.status

export function BriefsPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [statusFilter, setStatusFilter] = useState<RecruiterBriefsFilterState['status']>('')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [briefToDelete, setBriefToDelete] = useState<string | null>(null)
  const createCanvasOpenKeyRef = useRef(0)
  const { openCanvas, closeCanvas } = useArtifactCanvas()

  const { briefs: allBriefs, isLoading, error } = useBriefs()
  const { deleteBrief, isDeleting } = useDeleteBrief()
  const { updateBrief } = useUpdateBrief()
  const view: BriefsView = searchParams.get('view') === 'board' ? 'board' : 'list'

  const setView = useCallback(
    (next: BriefsView) => {
      const params = new URLSearchParams(searchParams)
      if (next === 'board') params.set('view', 'board')
      else params.delete('view')
      setSearchParams(params, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const moveBriefStatus = useCallback(
    (brief: Brief, status: string) => {
      const label = BRIEF_STATUS_COLUMNS.find((c) => c.id === status)?.label ?? status
      updateBrief(
        { id: brief.id, data: { status: status as Brief['status'] } },
        {
          onSuccess: () => toast.success(`${brief.title} moved to ${label}`),
          onError: (error: Error) => toast.error(error.message || 'Failed to update brief'),
        },
      )
    },
    [updateBrief],
  )

  const filterCounts = useMemo(() => computeRecruiterBriefStatusCounts(allBriefs), [allBriefs])
  const filterCtx = useMemo(() => ({ counts: filterCounts }), [filterCounts])

  const briefFilters: RecruiterBriefsFilterState = useMemo(
    () => ({ search: '', status: statusFilter }),
    [statusFilter],
  )

  const onFiltersChange = useCallback((next: RecruiterBriefsFilterState) => {
    setStatusFilter(next.status)
  }, [])

  const clearFilters = useCallback(() => {
    setStatusFilter('')
  }, [])

  const { filterBar } = useListFilterBar({
    listId: 'recruiterBriefs',
    definitions: RECRUITER_BRIEFS_FILTER_DEFINITIONS,
    filters: briefFilters,
    onFiltersChange,
    ctx: filterCtx,
    onClearAll: clearFilters,
  })

  const briefs = useMemo(() => {
    if (!statusFilter) return allBriefs
    return allBriefs.filter((b) => b.status === statusFilter)
  }, [allBriefs, statusFilter])

  useEffect(() => {
    return () => {
      closeCanvas()
    }
  }, [closeCanvas])

  useEffect(() => {
    if (statusFilter && filterCounts[statusFilter] === 0) {
      setStatusFilter('')
    }
  }, [statusFilter, filterCounts])

  const openCreateBriefCanvas = useCallback(() => {
    createCanvasOpenKeyRef.current += 1
    openCanvas({
      title: 'Create brief',
      preventAutoFocus: true,
      content: (
        <BriefCanvasForm
          key={`brief-create-${createCanvasOpenKeyRef.current}`}
          mode="create"
          onClose={closeCanvas}
          onSaved={(created: { id: string }) => {
            closeCanvas()
            navigateWithThreadPreservation(navigate, `/recruiter/briefs/${created.id}`, searchParams)
          }}
        />
      ),
    })
  }, [closeCanvas, navigate, openCanvas, searchParams])

  useEffect(() => {
    if (searchParams.get('create') !== '1') return
    openCreateBriefCanvas()
    const next = new URLSearchParams(searchParams)
    next.delete('create')
    setSearchParams(next, { replace: true })
  }, [openCreateBriefCanvas, searchParams, setSearchParams])

  const handleDelete = (briefId: string) => {
    setBriefToDelete(briefId)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    if (briefToDelete) {
      deleteBrief(briefToDelete, {
        onSuccess: () => {
          toast.success('Brief deleted successfully')
          setDeleteDialogOpen(false)
          setBriefToDelete(null)
        },
        onError: (error: Error) => {
          toast.error(error.message || 'Failed to delete brief')
        },
      })
    }
  }

  if (error) {
    return (
      <PageContainer>
        <div className="text-center py-12">
          <p className="text-destructive">Error loading briefs: {(error as Error).message}</p>
        </div>
      </PageContainer>
    )
  }

  const { title } = getPageHeader('/recruiter/briefs', 'Briefs', 'Manage your recruitment briefs and candidate searches')

  return (
    <PageContainer>
        <PageHeaderStack>
          <PageHeader
            leading={<PageHeaderLeading />}
            title={title}
            icon={<FileText className="h-5 w-5" />}
            actions={
              <PageHeaderActions
                primary={
                  <PageHeaderActionPrimary type="button" onClick={() => openCreateBriefCanvas()}>
                    <Plus className="h-4 w-4" aria-hidden />
                    Create Brief
                  </PageHeaderActionPrimary>
                }
              />
            }
          />

          <PageLineTabs
            value={view}
            onValueChange={(value) => setView(value as BriefsView)}
            ariaLabel="Briefs view"
            items={BRIEFS_VIEW_ITEMS}
          />
          {view === 'list' ? filterBar : null}
        </PageHeaderStack>

        {isLoading ? (
          <DashboardSectionSkeleton cards={4} grid />
        ) : view === 'board' && allBriefs.length > 0 ? (
          <SimpleKanban
            ariaLabel="Briefs board"
            columns={BRIEF_STATUS_COLUMNS}
            items={allBriefs}
            getId={briefId}
            getColumn={briefStatus}
            onMove={moveBriefStatus}
            emptyLabel="No briefs"
            renderCard={(brief: Brief, { dragHandleProps }: SimpleKanbanCardProps) => (
              <div {...dragHandleProps}>
                <BriefListCard
                  brief={brief}
                  onOpen={() =>
                    navigateWithThreadPreservation(navigate, `/recruiter/briefs/${brief.id}`, searchParams)
                  }
                />
              </div>
            )}
          />
        ) : briefs.length === 0 ? (
          <Card>
            <CardContent>
              <PaneListEmptyState
                icon={<FileText aria-hidden />}
                title="No briefs yet"
                description={
                  statusFilter
                    ? `No ${recruiterBriefStatusLabel(statusFilter)}`
                    : 'Create a brief to start searching for candidates'
                }
                action={
                  statusFilter ? (
                    <Button type="button" variant="outline" onClick={() => setStatusFilter('')}>
                      Show all briefs
                    </Button>
                  ) : (
                    <Button type="button" onClick={() => openCreateBriefCanvas()}>
                      <Plus className="mr-2 size-4" aria-hidden />
                      Create Brief
                    </Button>
                  )
                }
              />
            </CardContent>
          </Card>
        ) : (
          <PaneResponsiveCardGrid>
            {briefs.map((brief) => (
              <BriefListCard
                key={brief.id}
                brief={brief}
                className="h-full"
                onOpen={() =>
                  navigateWithThreadPreservation(navigate, `/recruiter/briefs/${brief.id}`, searchParams)
                }
                trailingActions={
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(brief.id)}>
                    <Trash2 className="size-4" />
                  </Button>
                }
              />
            ))}
          </PaneResponsiveCardGrid>
        )}

        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete brief</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this brief? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                disabled={isDeleting}
                className="bg-destructive text-destructive-foreground"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
</PageContainer>
  )
}
